import React, { useState, useRef } from 'react';
import { Upload, Download, Copy, FileText, Code } from 'lucide-react';

const MarkdownConverter: React.FC = () => {
  const [input, setInput] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // 복사 버튼 상태 관리
  const [copiedStates, setCopiedStates] = useState<{
    plainText: boolean;
    htmlPreview: boolean;
    htmlCode: boolean;
    plainDownload: boolean;
    htmlDownload: boolean;
  }>({
    plainText: false,
    htmlPreview: false,
    htmlCode: false,
    plainDownload: false,
    htmlDownload: false,
  });

  // 복사 상태 설정 헬퍼 함수
  const setCopiedState = (key: keyof typeof copiedStates, value: boolean) => {
    setCopiedStates(prev => ({ ...prev, [key]: value }));
    if (value) {
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 5000);
    }
  };

  // 마크다운을 일반 텍스트로 변환
  const convertToPlainText = (markdown: string): string => {
    return markdown
      // 헤더 제거 (# ## ### 등)
      .replace(/^#{1,6}\s+/gm, '')
      // 굵은 글씨 제거 (**text** or __text__)
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      // 기울임 제거 (*text* or _text_)
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      // 취소선 제거 (~~text~~)
      .replace(/~~(.*?)~~/g, '$1')
      // 인라인 코드 제거 (`code`)
      .replace(/`([^`]+)`/g, '$1')
      // 코드 블록 제거
      .replace(/```[\s\S]*?```/g, '')
      .replace(/^    .*/gm, '')
      // 링크 제거 [text](url)
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // 이미지 제거 ![alt](url)
      .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
      // 인용문 제거 (>)
      .replace(/^>\s*/gm, '')
      // 리스트 마커 제거 (- * +)
      .replace(/^[\s]*[-*+]\s+/gm, '')
      // 번호 리스트 제거 (1. 2. 등)
      .replace(/^[\s]*\d+\.\s+/gm, '')
      // 수평선 제거 (--- *** 등)
      .replace(/^[-*_]{3,}$/gm, '')
      // 테이블 제거
      .replace(/^\|.*\|$/gm, '')
      .replace(/^[-\s:]+$/gm, '')
      // 테이블 구분선 제거 (|---|---|)
      .replace(/^\|[\s\-:]+\|$/gm, '')
      // 여러 줄바꿈을 하나로
      .replace(/\n{3,}/g, '\n\n')
      // 앞뒤 공백 제거
      .trim();
  };

  // 마크다운을 HTML로 변환 (개선된 변환기)
  const convertToHTML = (markdown: string): string => {
    let html = markdown;

    // 테이블 변환 (완전히 새로운 로직)
    const processTable = (text: string): string => {
      const lines = text.split('\n');
      let inTable = false;
      let tableLines: string[] = [];
      let result: string[] = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // 테이블 행 감지 (| 로 시작하고 끝나는 라인)
        if (line.match(/^\|.*\|$/)) {
          if (!inTable) {
            inTable = true;
            tableLines = [];
          }
          tableLines.push(line);
        } 
        // 테이블 구분선 (|---|---| - 완전히 무시
        else if (line.match(/^\|[\s\-:]+\|$/)) {
          // 구분선은 테이블 구조 파악용으로만 사용하고 저장하지 않음
          continue;
        }
        // 테이블이 끝남
        else {
          if (inTable && tableLines.length >= 2) {
            // 테이블 처리
            const processedTable = convertTableToHTML(tableLines);
            result.push(processedTable);
            inTable = false;
            tableLines = [];
          } else if (inTable) {
            // 불완전한 테이블이면 원본 그대로
            result.push(...tableLines);
            inTable = false;
            tableLines = [];
          }
          
          if (line) {
            result.push(line);
          }
        }
      }
      
      // 마지막에 테이블이 있었다면 처리
      if (inTable && tableLines.length >= 2) {
        const processedTable = convertTableToHTML(tableLines);
        result.push(processedTable);
      } else if (inTable) {
        result.push(...tableLines);
      }
      
      return result.join('\n');
    };

    // 테이블을 HTML로 변환하는 함수
    const convertTableToHTML = (tableLines: string[]): string => {
      if (tableLines.length < 2) return tableLines.join('\n');
      
      let headerRow: string | null = null;
      let separatorIndex = -1;
      let bodyRows: string[] = [];
      
      // 헤더와 바디 분리
      for (let i = 0; i < tableLines.length; i++) {
        const line = tableLines[i].trim();
        if (line.match(/^\|[\s\-:]+\|$/)) {
          separatorIndex = i;
          headerRow = tableLines[i - 1];
          bodyRows = tableLines.slice(i + 1);
          break;
        }
      }
      
      if (!headerRow || separatorIndex === -1) {
        // 구분선이 없으면 첫 번째 행을 헤더로 처리
        headerRow = tableLines[0];
        bodyRows = tableLines.slice(1);
      }
      
      // 헤더 처리
      const headerCells = parseTableRow(headerRow);
      const headerHTML = headerCells.map(cell => {
        const processed = processTableCell(cell);
        return `<th>${processed}</th>`;
      }).join('');
      
      // 바디 행들 처리 (구분선 제외)
      const bodyHTML = bodyRows
        .filter(row => {
          // 구분선 패턴 제외
          if (row.trim().match(/^\|[\s\-:]+\|$/)) return false;
          
          // 대시만 포함된 행 제외 (예: |---|---|)
          const cells = parseTableRow(row);
          const isDashRow = cells.every(cell => 
            cell.trim() === '' || cell.trim().match(/^[\-\s:]+$/)
          );
          return !isDashRow;
        })
        .map(row => {
          const cells = parseTableRow(row);
          const cellsHTML = cells.map(cell => {
            const processed = processTableCell(cell);
            return `<td>${processed}</td>`;
          }).join('');
          return `<tr>${cellsHTML}</tr>`;
        }).join('');
      
      return `<table><thead><tr>${headerHTML}</tr></thead><tbody>${bodyHTML}</tbody></table>`;
    };

    // 테이블 행을 파싱하는 함수
    const parseTableRow = (row: string): string[] => {
      if (!row) return [];
      
      // | 로 분할하되, 첫 번째와 마지막 빈 요소 제거
      const parts = row.split('|');
      let cells: string[] = [];
      
      for (let i = 1; i < parts.length - 1; i++) {
        cells.push(parts[i].trim());
      }
      
      return cells;
    };

    // 테이블 셀 내용 처리
    const processTableCell = (cellContent: string): string => {
      if (!cellContent) return '';
      
      return cellContent
        // HTML 태그 보존 (br, ul, li 등)
        .replace(/<br\s*\/?>/gi, '<br>')
        .replace(/<ul>/gi, '<ul>')
        .replace(/<\/ul>/gi, '</ul>')
        .replace(/<li>/gi, '<li>')
        .replace(/<\/li>/gi, '</li>')
        // 굵은 글씨 처리
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/__(.*?)__/g, '<strong>$1</strong>')
        // 기울임 처리
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/_(.*?)_/g, '<em>$1</em>')
        // 취소선 처리
        .replace(/~~(.*?)~~/g, '<del>$1</del>')
        // 인라인 코드 처리
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        // 링크 처리
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>')
        // 이미지 처리
        .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img alt="$1" src="$2" />');
    };

    // 테이블 처리 실행
    html = processTable(html);

    html = html
      // 헤더 변환 (더 큰 헤더부터)
      .replace(/^###### (.*$)/gm, '<h6>$1</h6>')
      .replace(/^##### (.*$)/gm, '<h5>$1</h5>')
      .replace(/^#### (.*$)/gm, '<h4>$1</h4>')
      .replace(/^### (.*$)/gm, '<h3>$1</h3>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^# (.*$)/gm, '<h1>$1</h1>')
      // 굵은 글씨 변환
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // 기울임 변환
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // 취소선 변환
      .replace(/~~(.*?)~~/g, '<del>$1</del>')
      // 인라인 코드 변환
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // 링크 변환
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>')
      // 이미지 변환
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img alt="$1" src="$2" />')
      // 인용문 변환
      .replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>')
      // 수평선 변환
      .replace(/^[-*_]{3,}$/gm, '<hr>');

    // 코드 블록 변환 (먼저 처리)
    html = html.replace(/```(.*?)\n([\s\S]*?)```/g, '<pre><code class="language-$1">$2</code></pre>');

    // 리스트 변환 (개선된 버전)
    const lines = html.split('\n');
    const processedLines: string[] = [];
    let inList = false;
    let listType = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const unorderedMatch = line.match(/^(\s*)[-*+]\s+(.*)$/);
      const orderedMatch = line.match(/^(\s*)\d+\.\s+(.*)$/);
      
      if (unorderedMatch) {
        if (!inList || listType !== 'ul') {
          if (inList) processedLines.push(`</${listType}>`);
          processedLines.push('<ul>');
          inList = true;
          listType = 'ul';
        }
        processedLines.push(`<li>${unorderedMatch[2]}</li>`);
      } else if (orderedMatch) {
        if (!inList || listType !== 'ol') {
          if (inList) processedLines.push(`</${listType}>`);
          processedLines.push('<ol>');
          inList = true;
          listType = 'ol';
        }
        processedLines.push(`<li>${orderedMatch[2]}</li>`);
      } else {
        if (inList) {
          processedLines.push(`</${listType}>`);
          inList = false;
          listType = '';
        }
        processedLines.push(line);
      }
    }
    
    if (inList) {
      processedLines.push(`</${listType}>`);
    }

    html = processedLines.join('\n');

    // 문단 변환 (빈 줄로 구분된 텍스트를 <p>로 감싸기)
    html = html.replace(/\n\n+/g, '\n\n');
    const paragraphs = html.split('\n\n');
    html = paragraphs.map(para => {
      para = para.trim();
      if (!para) return '';
      // 이미 HTML 태그로 감싸진 경우는 그대로 둠
      if (para.match(/^<(h[1-6]|ul|ol|blockquote|pre|hr|table)/)) {
        return para;
      }
      // 일반 텍스트는 p 태그로 감싸기, 줄바꿈은 <br>로 변환
      return `<p>${para.replace(/\n/g, '<br>')}</p>`;
    }).filter(para => para).join('\n\n');

    return html;
  };



  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file && (file.type === 'text/markdown' || file.name.endsWith('.md'))) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        if (e.target?.result) {
          setInput(e.target.result as string);
        }
      };
      reader.readAsText(file);
    } else {
      alert('마크다운 파일(.md)을 선택해주세요.');
    }
  };

  const downloadFile = (): void => {
    const output = convertToHTML(input);
    const content = `<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>변환된 문서</title>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; color: #333; }
        h1 { font-size: 2.5em; margin-bottom: 0.5em; border-bottom: 3px solid #333; padding-bottom: 0.3em; }
        h2 { font-size: 2em; margin-top: 1.5em; margin-bottom: 0.5em; border-bottom: 2px solid #666; padding-bottom: 0.2em; }
        h3 { font-size: 1.5em; margin-top: 1.2em; margin-bottom: 0.5em; color: #444; }
        h4 { font-size: 1.3em; margin-top: 1em; margin-bottom: 0.5em; color: #555; }
        h5 { font-size: 1.1em; margin-top: 1em; margin-bottom: 0.5em; color: #666; }
        h6 { font-size: 1em; margin-top: 1em; margin-bottom: 0.5em; color: #777; font-weight: bold; }
        p { margin-bottom: 1em; text-align: justify; }
        ul, ol { margin-bottom: 1em; padding-left: 2em; }
        li { margin-bottom: 0.3em; }
        blockquote { margin: 1em 0; padding: 0.5em 1em; border-left: 4px solid #ddd; background-color: #f9f9f9; font-style: italic; }
        code { background-color: #f4f4f4; padding: 0.2em 0.4em; border-radius: 3px; font-family: 'Courier New', monospace; }
        pre { background-color: #f8f8f8; padding: 1em; border-radius: 5px; overflow-x: auto; border: 1px solid #e1e1e1; }
        pre code { background: none; padding: 0; }
        hr { margin: 2em 0; border: none; border-top: 2px solid #ddd; }
        a { color: #0066cc; text-decoration: none; }
        a:hover { text-decoration: underline; }
        img { max-width: 100%; height: auto; display: block; margin: 1em auto; }
        strong { font-weight: 600; }
        em { font-style: italic; color: #555; }
        del { color: #999; }
        table { width: 100%; border-collapse: collapse; margin: 1.5em 0; border: 1px solid #ddd; }
        th, td { padding: 0.75em 1em; text-align: left; border-bottom: 1px solid #ddd; }
        th { background-color: #f8f9fa; font-weight: 600; color: #333; border-bottom: 2px solid #ddd; }
        tr:nth-child(even) { background-color: #f9f9f9; }
        tr:hover { background-color: #f0f0f0; }
        td { vertical-align: top; }
    </style>
</head>
<body>
${output}
</body>
</html>`;
    
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'converted.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setCopiedState('htmlDownload', true);
  };

  const copyToClipboard = (): void => {
    navigator.clipboard.writeText(convertToHTML(input)).then(() => {
      setCopiedState('htmlCode', true);
    });
  };

  // HTML 미리보기 복사 함수 (렌더링된 내용 그대로 복사)
  const copyHTMLPreview = async (): Promise<void> => {
    try {
      const previewElement = document.querySelector('.preview-content');
      if (!previewElement) {
        throw new Error('미리보기 요소를 찾을 수 없습니다.');
      }

      // 선택 범위 생성
      const range = document.createRange();
      range.selectNodeContents(previewElement);
      
      // 선택 적용
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        
        // 복사 실행
        const success = document.execCommand('copy');
        
        // 선택 해제
        selection.removeAllRanges();
        
        if (success) {
          setCopiedState('htmlPreview', true);
        } else {
          throw new Error('복사에 실패했습니다.');
        }
      } else {
        throw new Error('선택 기능을 사용할 수 없습니다.');
      }
    } catch (error) {
      console.error('미리보기 복사 실패:', error);
      // 폴백: 텍스트만 복사
      try {
        const previewElement = document.querySelector('.preview-content');
        if (previewElement) {
          await navigator.clipboard.writeText(previewElement.textContent || '');
          setCopiedState('htmlPreview', true);
        }
      } catch (fallbackError) {
        console.error('폴백 복사도 실패:', fallbackError);
      }
    }
  };

  // HTML 미리보기용 스타일
  const previewStyles: React.CSSProperties = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    lineHeight: '1.6',
    color: '#333',
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-full mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* 헤더 */}
          <div className="bg-blue-600 text-white p-6">
            <h1 className="text-2xl font-bold mb-2">마크다운 변환기</h1>
            <p className="text-blue-100">마크다운을 일반 텍스트나 HTML로 변환해보세요</p>
          </div>

          {/* 컨트롤 패널 */}
          <div className="p-4 border-b bg-gray-50">
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Upload size={16} />
                파일 업로드
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".md,.markdown"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* 메인 컨텐츠 */}
          <div className="grid grid-cols-1 xl:grid-cols-3 min-h-96">
            {/* 입력 영역 */}
            <div className="p-4 border-r">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">마크다운 입력</h3>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="여기에 마크다운 텍스트를 입력하거나 파일을 업로드하세요..."
                className="w-full h-80 p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              />
            </div>

            {/* HTML 출력 영역 */}
            <div className="p-4 border-r">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-800">HTML 미리보기</h3>
                <div className="flex gap-2">
                  <button
                    onClick={copyHTMLPreview}
                    disabled={!input}
                    className="flex items-center gap-2 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    <Copy size={14} />
                    {copiedStates.htmlPreview ? 'Copied' : '미리보기 복사'}
                  </button>
                  
                  <button
                    onClick={copyToClipboard}
                    disabled={!input}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    <Code size={14} />
                    {copiedStates.htmlCode ? 'Copied' : 'HTML 복사'}
                  </button>
                  
                  <button
                    onClick={downloadFile}
                    disabled={!input}
                    className="flex items-center gap-2 px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    <Download size={14} />
                    {copiedStates.htmlDownload ? 'Copied' : '다운로드'}
                  </button>
                </div>
              </div>
              
              <div 
                className="bg-white p-4 border rounded min-h-80 max-h-80 overflow-auto"
                style={previewStyles}
              >
                <style>
                  {`
                    .preview-content h1 { font-size: 2.5em; margin-bottom: 0.5em; border-bottom: 3px solid #333; padding-bottom: 0.3em; }
                    .preview-content h2 { font-size: 2em; margin-top: 1.5em; margin-bottom: 0.5em; border-bottom: 2px solid #666; padding-bottom: 0.2em; }
                    .preview-content h3 { font-size: 1.5em; margin-top: 1.2em; margin-bottom: 0.5em; color: #444; }
                    .preview-content h4 { font-size: 1.3em; margin-top: 1em; margin-bottom: 0.5em; color: #555; }
                    .preview-content h5 { font-size: 1.1em; margin-top: 1em; margin-bottom: 0.5em; color: #666; }
                    .preview-content h6 { font-size: 1em; margin-top: 1em; margin-bottom: 0.5em; color: #777; font-weight: bold; }
                    .preview-content p { margin-bottom: 1em; }
                    .preview-content ul, .preview-content ol { margin-bottom: 1em; padding-left: 2em; }
                    .preview-content li { margin-bottom: 0.3em; }
                    .preview-content blockquote { margin: 1em 0; padding: 0.5em 1em; border-left: 4px solid #ddd; background-color: #f9f9f9; font-style: italic; }
                    .preview-content code { background-color: #f4f4f4; padding: 0.2em 0.4em; border-radius: 3px; font-family: 'Courier New', monospace; }
                    .preview-content pre { background-color: #f8f8f8; padding: 1em; border-radius: 5px; overflow-x: auto; border: 1px solid #e1e1e1; }
                    .preview-content pre code { background: none; padding: 0; }
                    .preview-content hr { margin: 2em 0; border: none; border-top: 2px solid #ddd; }
                    .preview-content a { color: #0066cc; text-decoration: none; }
                    .preview-content a:hover { text-decoration: underline; }
                    .preview-content img { max-width: 100%; height: auto; display: block; margin: 1em auto; }
                    .preview-content strong { font-weight: 600; }
                    .preview-content em { font-style: italic; color: #555; }
                    .preview-content del { color: #999; }
                    .preview-content table { width: 100%; border-collapse: collapse; margin: 1.5em 0; border: 1px solid #ddd; }
                    .preview-content th, .preview-content td { padding: 0.75em 1em; text-align: left; border-bottom: 1px solid #ddd; }
                    .preview-content th { background-color: #f8f9fa; font-weight: 600; color: #333; border-bottom: 2px solid #ddd; }
                    .preview-content tr:nth-child(even) { background-color: #f9f9f9; }
                    .preview-content tr:hover { background-color: #f0f0f0; }
                    .preview-content td { vertical-align: top; }
                  `}
                </style>
                <div 
                  className="preview-content"
                  dangerouslySetInnerHTML={{ __html: convertToHTML(input) }}
                />
              </div>
            </div>

            {/* 일반 텍스트 출력 영역 */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-800">일반 텍스트</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(convertToPlainText(input)).then(() => {
                        setCopiedState('plainText', true);
                      });
                    }}
                    disabled={!input}
                    className="flex items-center gap-2 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    <Copy size={14} />
                    {copiedStates.plainText ? 'Copied' : '복사'}
                  </button>
                  
                  <button
                    onClick={() => {
                      const content = convertToPlainText(input);
                      const blob = new Blob([content], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'converted.txt';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      setCopiedState('plainDownload', true);
                    }}
                    disabled={!input}
                    className="flex items-center gap-2 px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    <Download size={14} />
                    {copiedStates.plainDownload ? 'Copied' : '다운로드'}
                  </button>
                </div>
              </div>
              
              <textarea
                value={convertToPlainText(input)}
                readOnly
                className="w-full h-80 p-3 border border-gray-300 rounded-lg resize-none bg-gray-50 font-mono text-sm"
              />
            </div>
          </div>

          {/* 사용법 */}
          <div className="p-4 bg-gray-50 border-t">
            <details className="cursor-pointer">
              <summary className="font-medium text-gray-700 mb-2">사용법 보기</summary>
              <div className="text-sm text-gray-600 space-y-2">
                <p>• <strong>일반 텍스트 모드:</strong> 마크다운 문법을 모두 제거하고 순수한 텍스트만 추출합니다.</p>
                <p>• <strong>HTML 모드:</strong> 마크다운을 HTML 태그로 변환하여 웹에서 사용할 수 있게 합니다. 미리보기에서 실제 렌더링 결과를 확인할 수 있습니다.</p>
                <p>• 파일 업로드 버튼으로 .md 파일을 불러오거나, 직접 텍스트를 입력할 수 있습니다.</p>
                <p>• 변환된 결과를 클립보드에 복사하거나 파일로 다운로드할 수 있습니다.</p>
                <p>• HTML 다운로드 시 완전한 HTML 문서로 저장되어 브라우저에서 바로 열어볼 수 있습니다.</p>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkdownConverter;